<template>
  <div class="roles-main">
    <div class="inputs-wrap">
      <el-row :gutter="10">
        <el-col :xl="12" :lg="20" :md="24">
          <el-date-picker
            v-model="request.settlementDate"
            format="DD/MM/YYYY"
            value-format="YYYY-MM-DD"
            size="normal"
            type="datetimerange"
            range-separator=":"
            :start-placeholder="t('fields.startDate')"
            :end-placeholder="t('fields.endDate')"
            :shortcuts="shortcuts"
            :disabled-date="disabledDate"
            @change="handleDateChange"
            :editable="false"
            :clearable="false"
            :default-time="defaultTime"
            style="width: 100%;"
          />
        </el-col>
        <el-col :xl="3" :lg="6" :md="12">
          <el-input
            v-model="request.affiliateName"
            size="normal"
            class="input-small"
            :placeholder="t('fields.loginName')"
          />
        </el-col>
        <el-col :xl="6" :lg="10" :md="12">
          <el-select
            v-model="request.status"
            size="normal"
            :placeholder="t('fields.status')"
            class="filter-item"
            style="width: 100%;"
          >
            <el-option
              v-for="item in uiControl.status"
              :key="item.key"
              :label="item.displayName"
              :value="item.value"
            />
          </el-select>
        </el-col>
        <el-col :xl="3" :lg="8" :md="12">
          <div class="btn-grp">
            <el-button
              icon="el-icon-search"
              type="primary"
              @click="loadSettlementRecord()"
              size="normal"
            >
              {{ $t('fields.search') }}
            </el-button>
            <el-button size="normal" type="primary" plain @click="resetQuery()">
              {{ $t('fields.reset') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </div>
    <div style="width: 98%; margin: 10px auto; overflow: auto;">
      <table
        cellpadding="0"
        cellspacing="0"
        border="0"
        class="custom-table"
        style="width: 98%; margin: 15px auto;"
      >
        <thead>
          <tr>
            <th scope="col">{{ t('fields.loginName') }}</th>
            <th scope="col">{{ t('fields.settlementDate') }}</th>
            <th scope="col">{{ t('fields.recordTime') }}</th>
            <th scope="col">{{ t('fields.settlementType') }}</th>
            <th scope="col">{{ t('fields.commissionAmount') }}</th>
            <th scope="col">{{ t('fields.commissionRate') }}</th>
            <th scope="col">{{ t('fields.finalAmount') }}</th>
            <th scope="col">{{ t('fields.status') }}</th>
            <th scope="col">{{ t('fields.operate') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in page.records" :key="item.id">
            <td :data-label="t('fields.loginName')">{{ item.loginName }}</td>
            <td :data-label="t('fields.settlementDate')">
              <span v-if="item.settlementDate === null">-</span>
              <span v-if="item.settlementDate !== null">{{ item.settlementDate }}</span>
            </td>
            <td :data-label="t('fields.recordTime')">
              {{ item.recordTime }}
            </td>
            <td :data-label="t('fields.settlementType')">
              {{ t(`settlementType.${item.settlementType}`) }}
            </td>
            <td :data-label="t('fields.commissionAmount')">
              $
              <span v-formatter="{data: item.totalCommissionProfit, type: 'money'}" />
            </td>
            <td :data-label="t('fields.commissionRate')">{{ (item.commissionRate * 100).toFixed(2) }} %</td>
            <td :data-label="t('fields.finalAmount')">
              $
              <span v-formatter="{data: Math.max(0, item.finalProfit), type: 'money'}" />
            </td>
            <td :data-label="t('fields.status')">
              <el-tag
                v-if="item.status === 'CLEARED'"
                type="success"
                size="normal"
              >
                {{ t('settlementStatus.' + item.status) }}
              </el-tag>
              <el-tag
                v-else-if="item.status === 'CHECKING'"
                type="warning"
                size="normal"
              >
                {{ t('settlementStatus.' + item.status) }}
              </el-tag>
              <el-tag v-else type="info" size="normal">-</el-tag>
            </td>
            <td :data-label="t('fields.operate')">
              <el-link
                v-if="item.status === 'CHECKING' && item.finalProfit > 0"
                type="primary"
                :underline="false"
                @click="payItem(item)"
              >
                {{ t('fields.settlePay') }}
              </el-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="page.records.length === 0">
      <emptyComp />
    </div>
    <el-pagination
      class="pagination"
      @current-change="changePage"
      layout="total, prev, pager, next"
      style="margin: 5px;"
      :total="page.total"
      :page-size="request.size"
      :page-count="page.pages"
      :current-page="request.current"
    />
  </div>
</template>

<script setup>
import { onMounted, reactive } from 'vue'
import { useStore } from '@/store'
import moment from 'moment'
import { getPh2AffiliateSettlement, pay } from '@/api/affiliate-settlement'
import { useI18n } from 'vue-i18n'
import emptyComp from '@/components/empty'
import { ElMessage, ElMessageBox } from 'element-plus'

const store = useStore()
const { t } = useI18n()

const uiControl = reactive({
  dialogVisible: false,
  gameType: [
    { key: 1, displayName: t('gameType.SLOT'), value: 'SLOT' },
    { key: 2, displayName: t('gameType.LIVE'), value: 'LIVE' },
    { key: 3, displayName: t('gameType.FISH'), value: 'FISH' },
    { key: 4, displayName: t('gameType.SPORT'), value: 'SPORT' },
    { key: 5, displayName: t('gameType.ESPORT'), value: 'ESPORT' },
    { key: 6, displayName: t('gameType.POKER'), value: 'POKER' },
    { key: 7, displayName: t('gameType.LOTTERY'), value: 'LOTTERY' },
  ],
  status: [
    { key: 1, displayName: t('settlementStatus.CHECKING'), value: 'CHECKING' },
    { key: 2, displayName: t('settlementStatus.CLEARED'), value: 'CLEARED' },
  ],
})

const defaultTime = [
  new Date(2000, 1, 1, 0, 0, 0),
  new Date(2000, 1, 1, 23, 59, 59),
]
const shortcuts = [
  {
    text: t('fields.today'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .startOf('day')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.yesterday'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .subtract(1, 'days')
          .startOf('day')
          .format('x')
      )
      end.setTime(
        moment(end)
          .subtract(1, 'days')
          .endOf('day')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.thisWeek'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .startOf('isoWeek')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.lastWeek'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .subtract(1, 'weeks')
          .startOf('isoWeek')
          .format('x')
      )
      end.setTime(
        moment(end)
          .subtract(1, 'weeks')
          .endOf('isoWeek')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.thisMonth'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .startOf('month')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.lastMonth'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .subtract(1, 'months')
          .startOf('month')
          .format('x')
      )
      end.setTime(
        moment(end)
          .subtract(1, 'months')
          .endOf('month')
          .format('x')
      )
      return [start, end]
    },
  },
]

const request = reactive({
  size: 20,
  current: 1,
  settlementDate: [convertStartDate(new Date()), convertDate(new Date())],
  loginName: null,
  platform: null,
  gameType: [],
  status: null,
  downlineAffiliate: null,
})

const page = reactive({
  pages: 0,
  records: [],
  loading: false,
  total: 0,
  totalBet: 0,
  totalPayout: 0,
  totalCompanyProfit: 0,
})

function convertDate(date) {
  return moment(date).format('YYYY-MM-DD')
}

function convertStartDate(date) {
  return moment(date)
    .startOf('day')
    .format('YYYY-MM-DD')
}

function disabledDate(time) {
  const today = new Date()
  const sixMonthsAgo = moment().subtract(6, 'months')
  return (
    moment(time).isBefore(sixMonthsAgo, 'day') ||
    moment(time).isAfter(today, 'day')
  )
}

function handleDateChange([start, end]) {
  if (!start || !end) return

  const startDate = moment(start)
  const endDate = moment(end)
  const diffInDays = endDate.diff(startDate, 'days')

  if (diffInDays > 31) {
    ElMessage.warning({
      message: t('message.startEndMoreThanOneMonth'),
      type: 'error',
    })
    request.settlementDate = [convertStartDate(new Date()), convertDate(new Date())]
  }
}

function resetQuery() {
  request.settlementDate = [convertStartDate(new Date()), convertDate(new Date())]
  request.loginName = null
  request.status = null
}

async function loadSettlementRecord() {
  page.loading = true
  const requestCopy = { ...request }
  const query = {}
  Object.entries(requestCopy).forEach(([key, value]) => {
    if (value) {
      query[key] = value
    }
  })
  if (request.settlementDate !== null) {
    if (request.settlementDate.length === 2) {
      query.settlementDate = request.settlementDate.join(',')
    }
  }
  query.siteId = store.state.user.siteId
  const { data: ret } = await getPh2AffiliateSettlement(query)
  page.pages = ret.pages
  page.records = ret.records
  page.total = ret.total
  page.loading = false
}

function changePage(page) {
  if (request.current >= 1) {
    request.current = page
    loadSettlementRecord()
  }
}

async function payItem(item) {
  ElMessageBox.confirm(t('message.confirmToPay'), {
    title: t('message.confirmToPay'),
    confirmButtonText: t('fields.confirm'), // Replace with your translation key for "OK"
    cancelButtonText: t('fields.cancel'), // Optional: Replace with your translation key for "Cancel"
  })
    .then(async () => {
      await pay(item.id)
      ElMessage({
        message: t('message.commissionPaySuccess'),
        type: 'success',
      })
      loadSettlementRecord()
    })
    .catch(() => {
      // catch error
    })
}

onMounted(async () => {
  await loadSettlementRecord()
})
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.header-container {
  margin: 40px 0 20px;
  display: flex;
}

.box-card {
  ::v-deep(.el-card__body) {
    padding: 0;
  }
}

.inputs-wrap {
  margin: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  .input-small {
    width: 100%;
    // max-width: 200px;
    &.el-range-editor--small.el-input__inner {
      height: 40px;
      max-width: 300px;
    }
  }

  .btn-grp {
    display: flex;
  }
}

.el-pagination {
  display: inline-block;
}

.table-footer {
  float: right;
  font-size: small;
  margin: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 768px) {
  // .inputs-wrap {
  //   flex-direction: column;
  //   gap: 10px;
  //   .el-input--small {
  //     width: 100% !important;
  //     max-width: unset !important;
  //     margin: 0 !important;
  //     .el-button {
  //       margin: 0 !important;
  //     }
  //   }
  //   .input-small {
  //     max-width: unset;
  //     width: 100%;
  //   &.el-range-editor--small.el-input__inner {
  //     max-width: unset;
  //   }
  //   }
  // }
}
</style>
<style>
.el-col {
  margin-bottom: 10px;
}
</style>
